import type { AxiosResponse } from 'axios';
import React, { createContext, useCallback, useContext } from 'react';
import {
	extractOfDataQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	useProcessControl,
	useServiceAuthorization,
	useTreeFormCheckboxes,
} from '@gov-nx/core/hooks';
import {
	getRequiredDataBoxes,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { compose } from '@gov-nx/core/types';
import type { ServiceCode } from '../definitions/codes';
import { FormInstance, prepareSubmitData, useTree } from './FormDefinitions';
import type {
	FormData,
	ProcessControl,
	ServiceContextTypes,
} from './service.types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

export const ServiceContext =
	createContext<Nullable<ServiceContextTypes>>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

const AUTHORIZATION_SERVICE_NAME = 'UDAJE_ROB';

export const VypisUdajuZROBContextProvider = ({
	children,
	code,
}: ServiceContextProviderProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { toastMessageSuccess, toastMessageWarning } = useMessageEvents({});
	const { controls, setControls } = useProcessControl<ProcessControl>({
		isSubmitted: false,
	});
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const requiredDataBoxes = getRequiredDataBoxes(code, application);
	const { axiosInstance } = useAxiosInstance();
	const authContext = useAutorizaceContext();
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);
	const formDefinition = FormInstance({
		dataBoxTypes: requiredDataBoxes,
		isLowAuthorization,
	});

	const treeCheckboxes = useTreeFormCheckboxes({
		tree: useTree(),
		formMethods: formDefinition.formMethods,
	});

	const submitMutation = usePoMutation<AxiosResponse<void>, FormData>({
		mutationFn: compose(extractOfDataQuery(axiosInstance), prepareSubmitData),
		onSuccess: (response) => {
			setControls({ processLoading: false, isSubmitted: true });
			formDefinition.formReset();

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(tsn('form.messages.odeslano'));
			};

			if (authContext) {
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					response.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
		onError: (error) => {
			setControls({
				processError: error,
				processLoading: false,
				isSubmitted: false,
			});
			formDefinition.formReset();
		},
	});

	const handleSubmit = useCallback(() => {
		if (treeCheckboxes.isAnyFieldChecked()) {
			setControls({
				processError: null,
				processLoading: true,
				isSubmitted: false,
			});
			submitMutation.mutate(formDefinition.formMethods.getValues());
		} else {
			toastMessageWarning(tsn('form.messages.vyberte-moznost'));
		}
	}, [
		formDefinition.formMethods,
		setControls,
		submitMutation,
		toastMessageWarning,
		treeCheckboxes,
		tsn,
	]);

	return (
		<ServiceContext.Provider
			value={{
				code,
				controls,
				formDefinition,
				setControls,
				submitMutation,
				treeCheckboxes,
				onSubmit: formDefinition.formMethods.handleSubmit(handleSubmit),
				requiredDataBoxes: requiredDataBoxes,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ServiceContext.Provider>
	);
};

export const useVypisUdajuZROBContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
